/**
 * The two things a screen can ask of the frame around it.
 *
 * Both exist for one screen — Modding — and both are deliberately small. A screen that wants
 * its own chrome is a screen that has stopped being part of the console, and the answer to
 * "could this be a second shell" is always no.
 *
 * `TopBarSlot` puts a screen's own switch in the bar that is already on every screen, rather
 * than spending a row of the page on one. `useFullBleed` lets a tool fill the window: an editor
 * with a column of reading width around it is an editor with less code in it, and the padding
 * the rest of the app wants is padding this one does not.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';

/** Where `AppShell` leaves room in the top bar for whatever screen is open. */
export const TOP_BAR_SLOT = 'gb-topbar-screen';

export function TopBarSlot({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);

  // After the first paint, because the shell is this component's parent and its DOM does not
  // exist while this is rendering. One frame without the switch is not worth a ref forwarded
  // through the whole tree to avoid.
  React.useEffect(() => {
    setSlot(document.getElementById(TOP_BAR_SLOT));
  }, []);

  return slot === null ? null : createPortal(children, slot);
}

/**
 * Takes the padding and the reading width off the screen for as long as this is mounted.
 *
 * Set on the body rather than passed down, because what has to change is the shell's own
 * scroll area, which is two components above whoever is asking.
 */
export function useFullBleed(active: boolean): void {
  React.useEffect(() => {
    if (!active) return;
    document.body.dataset.fullBleed = 'true';
    // The scroll area stops scrolling here, and an area that stops scrolling keeps whatever
    // offset it was left on: arriving from a page that was scrolled down, or from this screen's
    // own welcome, the tool opens with its top edge above the window and no way to reach it.
    document.querySelector<HTMLElement>('.gb-os__main')?.scrollTo(0, 0);
    return () => {
      delete document.body.dataset.fullBleed;
    };
  }, [active]);
}
